import React, { useMemo, useCallback } from 'react';
import {
    Accordion,
    Badge,
    Title,
    Text,
    Box,
    Group,
    Stack,
    Skeleton,
} from '@mantine/core';
import _ from 'lodash';
import { useQuery } from '@apollo/client';

import { SapPriceChangeRequest } from '../../codegen/graphql';
import { trimLeadingZeros } from '../../utility/skuUtils';
import {
    getDefaultOutcomeMessage,
    getStatusColor,
    getPriceAdjustmentMessage,
    getSapPriceMismatch,
} from '../../helpers/sapOutcomeHelpers';
import { GET_SAP_OUTCOMES_FOR_REQUEST } from '../../apollo/queries/priceChangeRequest';
import { useRequestDrawer } from '../../context/RequestDrawerContext';
import formatCurrency from '../../utility/formatCurrency';
import { When } from '../common';

type Props = {
    requestId: string | null;
};

function RequestSapOutcome({ requestId }: Props) {
    const { flatRequest, adZoneStores } = useRequestDrawer();

    const {
        data: sapData,
        loading: sapLoading,
        error: sapError,
    } = useQuery(GET_SAP_OUTCOMES_FOR_REQUEST, {
        variables: { requestId: requestId! },
        skip: !requestId,
        fetchPolicy: 'cache-and-network',
    });

    const sapPriceChangeRequests: SapPriceChangeRequest[] =
        sapData?.getSapOutcomesForRequest ?? [];

    const storeNameMap = useMemo(() => {
        const map: Record<string, string> = {};
        if (adZoneStores) {
            adZoneStores.forEach((store: any) => {
                if (store.storeNo && store.name) {
                    map[store.storeNo] = store.name;
                }
            });
        }
        if (flatRequest?.storeNo && flatRequest?.storeName) {
            map[flatRequest.storeNo] = flatRequest.storeName;
        }
        return map;
    }, [adZoneStores, flatRequest]);

    const isInterimPriceChange = !_.isNil(flatRequest?.temporaryChangedPrice);
    const currency = flatRequest?.currency ?? 'ZAR';

    const renderStoreGroup = useCallback(
        (
            storeNo: string,
            storeName: string,
            requests: SapPriceChangeRequest[],
        ) => {
            const mainProduct = requests.find(r => !r.isFromRelatedSkus);
            const relatedSkus = requests.filter(r => r.isFromRelatedSkus);
            const sorted = mainProduct
                ? [mainProduct, ...relatedSkus]
                : relatedSkus;
            const storeOutcome = mainProduct?.outcome ?? sorted[0]?.outcome;

            return (
                <Box
                    key={storeNo}
                    className="border border-solid border-[#e5e7eb] rounded-lg bg-white mb-3 p-4"
                >
                    <Group justify="space-between" className="mb-3">
                        <Group gap="md">
                            <Badge variant="light" color="blue" size="sm">
                                {storeNo}
                            </Badge>
                            <Text size="sm" fw={400} c="dimmed">
                                {storeName}
                            </Text>
                        </Group>
                        <Group gap="md">
                            <Badge
                                color={getStatusColor(storeOutcome)}
                                variant="light"
                            >
                                {storeOutcome ?? 'Unknown'}
                            </Badge>
                            <Text size="sm" c="dimmed">
                                {getPriceAdjustmentMessage(mainProduct)}
                            </Text>
                        </Group>
                    </Group>

                    <Stack gap="xs">
                        {sorted.map(r => {
                            const mismatch = getSapPriceMismatch(r);

                            return (
                                <Stack key={r.id} gap={2} className="py-1.5">
                                    <Group gap={48}>
                                        <Text size="sm" fw={400} c="dark">
                                            SKU {trimLeadingZeros(r.sku)}
                                        </Text>

                                        <Group gap="xl">
                                            <Badge
                                                color={getStatusColor(
                                                    r.outcome,
                                                )}
                                                variant="light"
                                            >
                                                {r.outcome}
                                            </Badge>
                                            <Text size="sm" c="dimmed">
                                                {r.outcomeReason ??
                                                    getDefaultOutcomeMessage(
                                                        r.outcome,
                                                    )}
                                            </Text>
                                        </Group>
                                    </Group>
                                    <When condition={mismatch != null}>
                                        <Text size="xs" c="orange.7" fw={500}>
                                            SAP adjusted the price on
                                            implementation: requested{' '}
                                            {formatCurrency(
                                                mismatch?.requested ?? 0,
                                                currency,
                                            )}
                                            , SAP applied{' '}
                                            {formatCurrency(
                                                mismatch?.applied ?? 0,
                                                currency,
                                            )}
                                        </Text>
                                    </When>
                                </Stack>
                            );
                        })}
                    </Stack>
                </Box>
            );
        },
        [currency],
    );

    const [sapPriceChanges, interimSapPriceChanges] = useMemo(() => {
        if (isInterimPriceChange) {
            const priceChanges = sapPriceChangeRequests.filter(
                r => !r.isInterim,
            );
            const interimPriceChanges = sapPriceChangeRequests.filter(
                r => r.isInterim,
            );
            return [priceChanges, interimPriceChanges];
        }
        return [sapPriceChangeRequests, []];
    }, [isInterimPriceChange, sapPriceChangeRequests]);

    const showSapSkeleton =
        sapLoading &&
        (flatRequest?.latestStatus === 'Approved' ||
            flatRequest?.latestStatus === 'ApprovedSupplier');

    if (showSapSkeleton) {
        return (
            <div className="flex flex-col flex-1 shadow-sm bg-white rounded-lg mt-5 p-5 gap-4">
                <div className="flex items-center gap-3">
                    <Skeleton height={20} width={120} />
                    <Skeleton height={22} width={80} radius="xl" />
                </div>
                <Skeleton height={60} radius="md" />
                <Skeleton height={60} radius="md" />
            </div>
        );
    }

    if (sapError && sapPriceChangeRequests.length === 0) {
        return (
            <div className="flex flex-col flex-1 shadow-sm bg-white rounded-lg mt-5 p-5 gap-2">
                <Title order={4}>SAP Outcome</Title>
                <Text size="sm" c="dimmed">
                    SAP outcome unavailable
                </Text>
            </div>
        );
    }

    if (sapPriceChangeRequests.length === 0) {
        return null;
    }

    const isAllSuccessful = sapPriceChangeRequests.every(
        sapPriceChangeRequest => sapPriceChangeRequest.outcome === 'Success',
    );

    const isAllFailed = sapPriceChangeRequests.every(
        sapPriceChangeRequest => sapPriceChangeRequest.outcome === 'Failed',
    );

    const someProcessing = sapPriceChangeRequests.some(
        sapPriceChangeRequest => sapPriceChangeRequest.outcome === 'Processing',
    );

    const renderBadge = () => {
        if (someProcessing) {
            return (
                <Badge color="yellow" variant="light">
                    Processing
                </Badge>
            );
        }

        if (isAllSuccessful) {
            return (
                <Badge color="green" variant="light">
                    Successful
                </Badge>
            );
        }

        if (isAllFailed) {
            return (
                <Badge color="red" variant="light">
                    Failed
                </Badge>
            );
        }

        if (!isAllFailed && !isAllSuccessful) {
            return (
                <Badge color="red" variant="light">
                    Some Failed
                </Badge>
            );
        }

        return (
            <Badge color="red" variant="light">
                Unknown
            </Badge>
        );
    };

    const renderGrouped = (requests: SapPriceChangeRequest[]) => {
        const grouped = _.groupBy(requests, 'storeNo');
        return Object.entries(grouped).map(([storeNo, group]) => {
            const storeName = storeNameMap[storeNo] || '';
            return renderStoreGroup(storeNo, storeName, group);
        });
    };

    return (
        <div className="flex flex-col flex-1 shadow-sm bg-white rounded-lg mt-5">
            <div className="flex flex-col flex-1 gap-5">
                <Accordion
                    styles={{
                        control: {
                            paddingLeft: 20,
                            paddingRight: 20,
                            borderRadius: 8,
                        },
                        item: {
                            border: 'none',
                        },
                    }}
                >
                    <Accordion.Item value="collapsible">
                        <Accordion.Control styles={{}}>
                            <div className="flex items-center gap-5">
                                <Title order={4}>SAP Outcome</Title>
                                {renderBadge()}
                            </div>
                        </Accordion.Control>
                        <Accordion.Panel>
                            {isInterimPriceChange ? (
                                <>
                                    {interimSapPriceChanges.length > 0 && (
                                        <div>
                                            <Title
                                                order={6}
                                                className="pl-2 mb-2"
                                            >
                                                Interim Price Changes
                                            </Title>
                                            {renderGrouped(
                                                interimSapPriceChanges,
                                            )}
                                        </div>
                                    )}
                                    {sapPriceChanges.length > 0 && (
                                        <div>
                                            <Title
                                                order={6}
                                                className="pl-2 mb-2"
                                            >
                                                Final Price Changes
                                            </Title>
                                            {renderGrouped(sapPriceChanges)}
                                        </div>
                                    )}
                                </>
                            ) : (
                                renderGrouped(sapPriceChanges)
                            )}
                        </Accordion.Panel>
                    </Accordion.Item>
                </Accordion>
            </div>
        </div>
    );
}

export default RequestSapOutcome;
